import { LanguageType, SearchPredicate } from '@nx-smartmonkey/shared/domain';
import { PaginatedResult } from '@nx-smartmonkey/shared/interfaces';
import { Organization } from '../domain/backoffice/organization/Organization';

import { Project } from '../domain/backoffice/project/Project';
import { User } from '../domain/backoffice/user/User';
import { PlanType } from '../domain/subscription/Subscription';
import { Translation } from '../domain/translation/Translation';
import { TranslationDomain } from '../domain/translation/TranslationDomain';
import { ContractData } from '../hooks/backoffice/subscriptions/useUpdateSubscriptionContract';
import { backofficeRepository } from '../repositories/backoffice';
import { BackofficeRepository } from '../repositories/backoffice/BackofficeRepository';
import { translationsRepository } from '../repositories/translations';
import { TranslationsRepository } from '../repositories/translations/TranslationsRepository';

export class BackofficeService {
  constructor(
    private backofficeRepository: BackofficeRepository,
    private translationsRepository: TranslationsRepository
  ) {}

  async changePlan({
    user_id,
    extra_vehicles,
    extra_supervisors,
    extra_projects,
    extra_users,
    plan,
    traffic,
    current_period_end,
  }: {
    user_id: string;
    extra_vehicles?: number;
    extra_supervisors?: number;
    extra_projects?: number;
    extra_users?: number;
    plan?: PlanType;
    traffic?: boolean;
    current_period_end?: Date;
  }): Promise<void> {
    await this.backofficeRepository.changePlan({
      user_id,
      extra_vehicles,
      extra_supervisors,
      extra_projects,
      extra_users,
      plan,
      traffic,
      current_period_end,
    });
  }

  async cancelSubscription({ userId }: { userId: string }): Promise<void> {
    await this.backofficeRepository.cancelSubscription({ userId });
  }

  async createTrialSubscription({ organizationId, days }: { organizationId: string; days: number }): Promise<void> {
    await this.backofficeRepository.createTrialSubscription({ organizationId, days });
  }

  async searchOrganizations({
    offset,
    limit,
    sort_by,
    sort_direction,
    predicates,
  }: {
    offset?: number;
    limit?: number;
    sort_by?: string;
    sort_direction?: string;
    predicates?: Array<SearchPredicate>;
  }): Promise<PaginatedResult<Organization>> {
    const organizationsPagination = await this.backofficeRepository.searchOrganizations({
      offset,
      limit,
      sort_by,
      sort_direction,
      predicates,
    });

    return organizationsPagination;
  }

  async retrieveOrganization({ organizationId }: { organizationId: string }): Promise<Organization> {
    const organization = await this.backofficeRepository.retrieveOrganization({ organizationId });
    return organization;
  }

  async searchProjects({
    offset,
    limit,
    sort_by,
    sort_direction,
    predicates,
  }: {
    offset?: number;
    limit?: number;
    sort_by?: string;
    sort_direction?: string;
    predicates?: Array<SearchPredicate>;
  }): Promise<PaginatedResult<Project>> {
    const projectsPagination = await this.backofficeRepository.searchProjects({
      offset,
      limit,
      sort_by,
      sort_direction,
      predicates,
    });

    return projectsPagination;
  }

  async retrieveProject({ projectId }: { projectId: string }): Promise<Project> {
    const project = await this.backofficeRepository.retrieveProject({ projectId });
    return project;
  }

  async searchUsers({
    offset,
    limit,
    sort_by,
    sort_direction,
    predicates,
  }: {
    offset?: number;
    limit?: number;
    sort_by?: string;
    sort_direction?: string;
    predicates?: Array<SearchPredicate>;
  }): Promise<PaginatedResult<User>> {
    const usersPagination = await this.backofficeRepository.searchUsers({
      offset,
      limit,
      sort_by,
      sort_direction,
      predicates,
    });

    return usersPagination;
  }

  async retrieveUser({ userId }: { userId: string }): Promise<User> {
    const user = await this.backofficeRepository.retrieveUser({ userId });
    return user;
  }

  async validateUser({ user }: { user: User }): Promise<User> {
    await this.backofficeRepository.validateUser({ userId: user.id });
    user.validateEmail();

    return user;
  }

  async searchTranslations({
    language,
    offset,
    limit,
    sort_by,
    sort_direction,
    predicates,
  }: {
    language: LanguageType;
    offset?: number;
    limit?: number;
    sort_by?: string;
    sort_direction?: string;
    predicates?: Array<SearchPredicate>;
  }): Promise<PaginatedResult<Translation>> {
    const translationsPagination = await this.translationsRepository.search({
      language,
      offset,
      limit,
      sort_by,
      sort_direction,
      predicates,
    });

    return translationsPagination;
  }

  async createTranslation({
    language,
    key,
    value,
    domain,
  }: {
    language: LanguageType;
    key: string;
    value: string;
    domain: TranslationDomain;
  }): Promise<void> {
    return this.translationsRepository.createTranslation({ language, key, value, domain });
  }

  async updateTranslation({
    language,
    key,
    value,
    domain,
  }: {
    language: LanguageType;
    key: string;
    value: string;
    domain: TranslationDomain;
  }) {
    return this.translationsRepository.updateTranslation({ language, key, value, domain });
  }

  async deleteTranslation({ key, domain }: { key: string; domain: TranslationDomain }) {
    return this.translationsRepository.deleteTranslation({ key, domain });
  }

  async updateUser({
    userId,
    newEmail,
    newPassword,
  }: {
    userId: string;
    newEmail?: string;
    newPassword?: string;
  }): Promise<void> {
    await this.backofficeRepository.updateUser({
      userId,
      newEmail,
      newPassword,
    });
  }

  async updateSubscriptionSMS({
    organizationId,
    enabled,
    from,
    increasedCredit,
    autoRechargeAmount,
    autoRechargeAt,
    autoRechargeEnabled,
    autoRechargePrice,
  }: {
    organizationId: string;
    enabled?: boolean;
    from?: string;
    increasedCredit?: number;
    autoRechargeAmount?: number;
    autoRechargeAt?: number;
    autoRechargeEnabled?: boolean;
    autoRechargePrice?: number;
  }): Promise<void> {
    await this.backofficeRepository.updateSubscriptionSMS({
      organizationId,
      enabled,
      from,
      increasedCredit,
      autoRechargeAmount,
      autoRechargeAt,
      autoRechargeEnabled,
      autoRechargePrice,
    });
  }

  async updateSubscriptionWhatsApp({
    organizationId,
    enabled,
    from,
    subaccountId,
    increasedCredit,
  }: {
    organizationId: string;
    enabled?: boolean;
    from?: string;
    subaccountId?: string;
    increasedCredit?: number;
  }): Promise<void> {
    await this.backofficeRepository.updateSubscriptionWhatsApp({
      organizationId,
      enabled,
      from,
      subaccountId,
      increasedCredit,
    });
  }

  async updateSubscriptionGeocode({
    organizationId,
    increasedCredit,
  }: {
    organizationId: string;
    increasedCredit: number;
  }): Promise<void> {
    await this.backofficeRepository.updateSubscriptionGeocode({
      organizationId,
      increasedCredit,
    });
  }

  async updateSubscriptionContract({
    organizationId,
    data,
  }: {
    organizationId: string;
    data: ContractData;
  }): Promise<void> {
    await this.backofficeRepository.updateSubscriptionContract({
      organizationId,
      data,
    });
  }

  async updateProjectManagedVehicles({
    projectId,
    managedVehicles,
    prorate,
  }: {
    projectId: string;
    managedVehicles: number | null;
    prorate: boolean;
  }): Promise<void> {
    await this.backofficeRepository.updateProjectManagedVehicles({
      projectId,
      managedVehicles,
      prorate,
    });
  }

  async updateProjectTraffic({ projectId, traffic }: { projectId: string; traffic: boolean }): Promise<void> {
    await this.backofficeRepository.updateProjectTraffic({
      projectId,
      traffic,
    });
  }

  async updateWhatsAppTemplate({
    projectId,
    id,
    subaccountId,
    from,
    enabled,
    templateId,
    replacement_values,
  }: {
    projectId: string;
    id: string;
    subaccountId?: string | null;
    from?: string | null;
    enabled?: boolean;
    templateId?: string | null;
    replacement_values?: Record<string, string>;
  }): Promise<void> {
    await this.backofficeRepository.updateWhatsAppTemplate({
      projectId,
      id,
      subaccountId,
      from,
      enabled,
      templateId,
      replacement_values,
    });
  }
}

export const backofficeService = new BackofficeService(backofficeRepository, translationsRepository);
